import React, { useState, useEffect, useCallback } from 'react';
import './PersonalInfo.css';

const PROVINCIAS = [
  'Buenos Aires', 'Catamarca', 'Chaco', 'Chubut', 'Ciudad Autónoma de Buenos Aires',
  'Córdoba', 'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja',
  'Mendoza', 'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis',
  'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán',
];

const VACIO = {
  nombre: '', telefono: '', direccion: '',
  localidad: '', provincia: '', codigo_postal: '', pais: 'Argentina',
};

/**
 * Profile and shipping address.
 *
 * This form used to be write-only: it fetched /api/infocuentas purely to test
 * for a 401 and threw the body away, so saved details were never shown back.
 * It also collected provincia, ciudad and a date of birth that were never
 * sent, and mashed the postal code into the street string. Every field here
 * now maps to a real column.
 */
function PersonalInfo() {
  const [datos, setDatos] = useState(VACIO);
  const [estado, setEstado] = useState('cargando');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        const r = await fetch('/api/infocuentas');
        if (cancelado) return;

        if (r.status === 401) { setEstado('sin-sesion'); return; }

        const d = await r.json();
        if (cancelado) return;

        // Show what is already saved instead of an empty form.
        setDatos({
          nombre: d.nombre || '',
          telefono: d.telefono || '',
          direccion: d.direccion || '',
          localidad: d.localidad || '',
          provincia: d.provincia || '',
          codigo_postal: d.codigo_postal || '',
          pais: d.pais || 'Argentina',
        });
        setEstado('listo');
      } catch (error) {
        if (!cancelado) setEstado('error');
        console.error('Error al cargar los datos de la cuenta:', error);
      }
    })();

    return () => { cancelado = true; };
  }, []);

  const cambiar = useCallback((campo) => (e) => {
    setDatos((prev) => ({ ...prev, [campo]: e.target.value }));
    setMensaje(null);
  }, []);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setMensaje(null);

    try {
      const r = await fetch('/api/guardar_infocuentas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(datos),
      });

      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMensaje({ tipo: 'ok', texto: 'Datos guardados.' });
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'No pudimos guardar los datos. Probá de nuevo.' });
      console.error('Error al guardar:', error);
    } finally {
      setGuardando(false);
    }
  };

  if (estado === 'sin-sesion') return null;

  if (estado === 'cargando') {
    return (
      <div className="pi-section">
        <div className="pi-skeleton" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => <span key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="pi-section">
      <header className="pi-header">
        <h2>Mis datos</h2>
        <p>Los usamos para coordinar las entregas con la tienda.</p>
      </header>

      <form id="pi-formulario" className="pi-container" onSubmit={guardar}>
        <div className="pi-grid">
          <div className="pi-form-duo">
            <label htmlFor="nombre">Nombre y apellido</label>
            <input
              type="text" id="nombre" autoComplete="name"
              value={datos.nombre} onChange={cambiar('nombre')} required
            />
          </div>

          <div className="pi-form-duo">
            <label htmlFor="telefono">Teléfono</label>
            <input
              type="tel" id="telefono" autoComplete="tel" placeholder="11 5555 5555"
              value={datos.telefono} onChange={cambiar('telefono')}
            />
          </div>

          <div className="pi-form-duo pi-form-ancho">
            <label htmlFor="direccion">Calle y número</label>
            <input
              type="text" id="direccion" autoComplete="street-address"
              value={datos.direccion} onChange={cambiar('direccion')}
            />
          </div>

          <div className="pi-form-duo">
            <label htmlFor="localidad">Localidad</label>
            <input
              type="text" id="localidad" autoComplete="address-level2"
              value={datos.localidad} onChange={cambiar('localidad')}
            />
          </div>

          <div className="pi-form-duo">
            <label htmlFor="provincia">Provincia</label>
            <select
              id="provincia" autoComplete="address-level1"
              value={datos.provincia} onChange={cambiar('provincia')}
            >
              <option value="">Elegí una provincia</option>
              {PROVINCIAS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="pi-form-duo">
            <label htmlFor="codigo_postal">Código postal</label>
            <input
              type="text" id="codigo_postal" autoComplete="postal-code" placeholder="C1425"
              value={datos.codigo_postal} onChange={cambiar('codigo_postal')}
            />
          </div>

          <div className="pi-form-duo">
            <label htmlFor="pais">País</label>
            <input
              type="text" id="pais" autoComplete="country-name"
              value={datos.pais} onChange={cambiar('pais')}
            />
          </div>
        </div>

        <div className="pi-acciones">
          <button type="submit" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>
          {mensaje && (
            <p className={`pi-mensaje pi-mensaje-${mensaje.tipo}`} role="status">
              {mensaje.texto}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}

export default PersonalInfo;
